import BaseWidgetController from './base_widget_controller';
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
} from 'chart.js';
import {
  GREEN_400,
  YELLOW_500,
  BLUE_400,
  RED_500,
  GRAY_700,
} from '../../../shared/colors';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip);

/**
 * Controller for the Task Status Breakdown widget.
 * Displays a stacked bar chart showing task completion status:
 * - Completed (green)
 * - In Progress (amber/yellow)
 * - Not Started (gray) with Overdue (red) stacked inside
 */
export default class extends BaseWidgetController {
  static targets = [...BaseWidgetController.targets, 'chart'];
  static values = {
    ...BaseWidgetController.values,
    overdueLabel: String,
    onTimeLabel: String,
    totalNotStartedLabel: String,
  };

  connect() {
    super.connect();
    this.chart = null;
  }

  disconnect() {
    super.disconnect();
    this.destroyChart();
  }

  showLoading() {
    super.showLoading();
    if (this.hasChartTarget) {
      this.chartTarget.classList.add('opacity-50');
    }
  }

  hideLoading() {
    super.hideLoading();
    if (this.hasChartTarget) {
      this.chartTarget.classList.remove('opacity-50');
    }
  }

  renderData(data) {
    super.renderData(data);
    this.renderChart(data);
  }

  renderChart(data) {
    if (!this.hasChartTarget) return;

    this.destroyChart();

    const { completed, in_progress, not_started, overdue } = data;
    const ctx = this.chartTarget.getContext('2d');

    // Store not_started total for tooltip access
    this.notStartedTotal = not_started;

    // Calculate non-overdue not_started (the portion that's on time)
    const notStartedOnTime = Math.max(0, not_started - overdue);

    const maxValue = Math.max(completed, in_progress, not_started, 1);

    this.chart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['', '', ''],
        datasets: [
          // Main bars (Completed, In Progress, Not Started on time)
          {
            label: 'Status',
            data: [completed, in_progress, notStartedOnTime],
            backgroundColor: [GREEN_400, YELLOW_500, BLUE_400],
            hoverBackgroundColor: [GREEN_400, YELLOW_500, BLUE_400],
            borderWidth: 0,
            borderRadius: 4,
            barThickness: 100,
          },
          // Overdue (stacked on Not Started only)
          {
            label: this.overdueLabelValue,
            data: [0, 0, overdue],
            backgroundColor: ['transparent', 'transparent', RED_500],
            hoverBackgroundColor: ['transparent', 'transparent', RED_500],
            borderWidth: 0,
            borderRadius: 4,
            barThickness: 100,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const datasetIndex = context.datasetIndex;
                const dataIndex = context.dataIndex;
                const value = context.raw;

                // Not Started bar (index 2) - show breakdown
                if (dataIndex === 2) {
                  if (datasetIndex === 0) {
                    // Hovering on "on time" portion (blue)
                    return [
                      `${this.onTimeLabelValue}: ${this.formatNumber(value)}`,
                      `${this.totalNotStartedLabelValue}: ${this.formatNumber(this.notStartedTotal)}`,
                    ];
                  } else {
                    // Hovering on "overdue" portion (red)
                    return [
                      `${this.overdueLabelValue}: ${this.formatNumber(value)}`,
                      `${this.totalNotStartedLabelValue}: ${this.formatNumber(this.notStartedTotal)}`,
                    ];
                  }
                }

                // Completed and In Progress bars - simple display
                return this.formatNumber(value);
              },
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: {
              display: false,
            },
            ticks: {
              color: GRAY_700,
              font: {
                size: 12,
              },
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            suggestedMax: maxValue * 1.1,
            grid: {
              display: false,
            },
            ticks: {
              color: GRAY_700,
              font: {
                size: 12,
              },
              callback: (value) => this.formatNumber(value),
            },
          },
        },
      },
    });
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
